import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";

import { IMG, SERVICES, TESTIMONIALS } from "../lib/assets";
import { Reveal, AnimatedHeading, FadeImage } from "../components/Reveal";
import { MandalaImprint, PaisleyDivider } from "../components/Motifs";
import Marquee from "../components/Marquee";
import { PageShell } from "../components/PageShell";

const EASE = [0.16, 1, 0.3, 1];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);

  return (
    <section ref={ref} className="relative h-screen w-full overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={IMG.heroBride}
          alt="Bridal artistry at Zaya & Zari"
          className="h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/40 to-ink-900/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink-900/70 to-transparent" />
      </motion.div>

      <MandalaImprint
        className="absolute -right-40 top-20"
        size={620}
        opacity={0.07}
      />

      <motion.div
        style={{ y: textY }}
        className="relative mx-auto flex h-full max-w-wide flex-col justify-end px-6 pb-24 sm:px-12"
      >
        <motion.p
          className="overline text-gold"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.9 }}
        >
          Royal heritage · Earthy soul
        </motion.p>

        <h1 className="mt-6 max-w-5xl font-display text-[15vw] font-light leading-[0.85] tracking-tight text-cream-50 sm:text-[12vw] lg:text-[9rem]">
          <AnimatedHeading as="span" text="Opulence," delay={0.4} className="block" />
          <span className="block italic text-gold">
            <AnimatedHeading as="span" text="rooted in earth." delay={0.7} />
          </span>
        </h1>

        <motion.div
          className="mt-10 flex flex-col items-start gap-6 sm:flex-row sm:items-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.9 }}
        >
          <p className="max-w-md text-base text-cream-50/75 leading-relaxed">
            A full-service luxury beauty atelier in the heart of Rajasthan —
            where hair, skin, bridal and nail artistry are woven like threads of gold.
          </p>
          <div className="flex gap-4">
            <Link
              to="/services"
              data-testid="hero-explore-btn"
              className="rounded-full bg-gold px-7 py-4 text-xs uppercase tracking-[0.2em] text-ink-900 transition-colors hover:bg-antique"
            >
              Explore Services
            </Link>
            <Link
              to="/contact"
              data-testid="hero-book-btn"
              className="rounded-full border border-gold/40 px-7 py-4 text-xs uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold/10"
            >
              Book a Visit
            </Link>
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        className="absolute bottom-8 left-1/2 flex -translate-x-1/2 flex-col items-center gap-2 text-cream-50/50"
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
      >
        <span className="overline text-[0.6rem]">Scroll</span>
        <ArrowDown size={16} />
      </motion.div>
    </section>
  );
}

function StorySection() {
  return (
    <section className="relative overflow-hidden py-28 sm:py-40">
      <div className="mx-auto grid max-w-wide grid-cols-1 items-center gap-16 px-6 sm:px-12 md:grid-cols-[1fr_1.618fr] lg:gap-28">
        <div>
          <Reveal>
            <p className="overline text-gold">Our Story</p>
          </Reveal>
          <AnimatedHeading
            text="Where heritage meets the modern muse."
            className="mt-6 font-display text-4xl font-light leading-tight text-cream-50 sm:text-5xl"
          />
          <Reveal delay={0.2}>
            <p className="mt-8 text-cream-50/70 leading-relaxed">
              Zaya means radiance. Zari is the golden thread woven into India's
              finest textiles. Together they shape our philosophy — beauty that
              shines from within, finished with meticulous, handcrafted detail.
            </p>
            <p className="mt-4 text-cream-50/70 leading-relaxed">
              Our artists blend time-honoured Indian rituals with contemporary
              technique, in a sanctuary designed around calm, warmth and gold.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <Link
              to="/about"
              data-testid="story-about-link"
              className="group mt-10 inline-flex items-center gap-3 text-sm uppercase tracking-[0.2em] text-gold"
            >
              Discover our atelier
              <ArrowRight
                size={18}
                className="transition-transform group-hover:translate-x-2"
              />
            </Link>
          </Reveal>
        </div>

        <div className="relative">
          <div className="absolute -left-6 -top-6 h-32 w-32 rounded-3xl maroon-bg opacity-80 md:-left-10 md:-top-10 md:h-48 md:w-48" />
          <FadeImage
            src={IMG.interiorChairs}
            alt="The Zaya & Zari atelier interior"
            className="relative aspect-[4/5] w-full"
          />
          <div className="absolute -bottom-8 -right-4 w-1/2 rounded-2xl border border-gold/30 bg-ink-800 p-6 md:-right-10">
            <p className="font-display text-5xl text-gold">12+</p>
            <p className="mt-2 text-xs uppercase tracking-[0.2em] text-cream-50/60">
              Years crafting beauty
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function ServicesPreview() {
  const [active, setActive] = useState(0);

  return (
    <section className="relative bg-ink-800/40 py-28 sm:py-40">
      <div className="mx-auto max-w-wide px-6 sm:px-12">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div>
            <Reveal>
              <p className="overline text-gold">What we do</p>
            </Reveal>
            <AnimatedHeading
              text="A full spectrum of artistry."
              className="mt-5 font-display text-4xl font-light text-cream-50 sm:text-6xl"
            />
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/services"
              className="link-underline text-sm uppercase tracking-[0.2em] text-cream-50/70"
            >
              View all services
            </Link>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-10 lg:grid-cols-[1.618fr_1fr]">
          <div>
            {SERVICES.map((s, i) => (
              <Link
                key={s.id}
                to="/services"
                data-testid={`service-row-${s.id}`}
                onMouseEnter={() => setActive(i)}
                className="group flex items-center justify-between border-t border-cream-50/10 py-7 transition-colors last:border-b hover:bg-cream-50/[0.02]"
              >
                <div className="flex items-baseline gap-5 sm:gap-8">
                  <span className="font-display text-lg text-gold/70">{s.no}</span>
                  <div>
                    <h3 className="font-display text-3xl font-light text-cream-50 transition-colors group-hover:text-gold sm:text-5xl">
                      {s.name}
                    </h3>
                    <p className="mt-1 hidden text-sm text-cream-50/50 sm:block">
                      {s.tagline}
                    </p>
                  </div>
                </div>
                <ArrowRight
                  size={26}
                  className="text-cream-50/40 transition-all group-hover:translate-x-2 group-hover:text-gold"
                />
                {/* mobile inline image */}
                <img
                  src={s.image}
                  alt={s.name}
                  className="ml-4 h-16 w-16 shrink-0 rounded-xl object-cover sm:hidden"
                />
              </Link>
            ))}
          </div>

          <div className="relative hidden overflow-hidden rounded-3xl lg:block">
            {SERVICES.map((s, i) => (
              <motion.img
                key={s.id}
                src={s.image}
                alt={s.name}
                className="absolute inset-0 h-full w-full object-cover"
                initial={false}
                animate={{ opacity: active === i ? 1 : 0, scale: active === i ? 1 : 1.08 }}
                transition={{ duration: 0.8, ease: EASE }}
              />
            ))}
            <div className="absolute inset-0 bg-gradient-to-t from-ink-900/50 to-transparent" />
          </div>
        </div>
      </div>
    </section>
  );
}

function GalleryStrip() {
  const items = [
    { src: IMG.brideGold, span: "md:col-span-2 md:row-span-2" },
    { src: IMG.spaCream, span: "" },
    { src: IMG.nailsArt, span: "" },
    { src: IMG.hairStyle, span: "md:col-span-2" },
    { src: IMG.brideSari, span: "" },
    { src: IMG.interiorMirror, span: "" },
  ];
  return (
    <section className="py-28 sm:py-40">
      <div className="mx-auto max-w-wide px-6 sm:px-12">
        <div className="flex flex-col items-center text-center">
          <Reveal>
            <p className="overline text-gold">The Gallery</p>
          </Reveal>
          <AnimatedHeading
            text="Moments woven in gold"
            className="mt-5 font-display text-4xl font-light text-cream-50 sm:text-6xl"
          />
        </div>

        <div className="mt-16 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4 md:auto-rows-[230px]">
          {items.map((it) => (
            <FadeImage
              key={it.src}
              src={it.src}
              alt="Zaya & Zari gallery"
              className={`group h-full w-full ${it.span}`}
              imgClassName="transition-transform duration-700 group-hover:scale-105"
            />
          ))}
        </div>

        <div className="mt-12 flex justify-center">
          <Link
            to="/gallery"
            data-testid="gallery-strip-link"
            className="border border-gold/40 px-8 py-4 text-xs uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold/10 rounded-full"
          >
            View Full Gallery
          </Link>
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="relative overflow-hidden maroon-bg py-28 sm:py-40">
      <MandalaImprint
        className="absolute -left-32 -top-32"
        size={500}
        opacity={0.08}
      />
      <div className="mx-auto max-w-wide px-6 sm:px-12">
        <div className="text-center">
          <Reveal>
            <p className="overline text-gold">Kind Words</p>
          </Reveal>
        </div>
        <div className="mt-16 grid grid-cols-1 gap-10 md:grid-cols-3">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.12}>
              <div className="flex h-full flex-col rounded-2xl border border-gold/20 bg-ink-900/30 p-8 backdrop-blur-sm">
                <span className="font-display text-6xl leading-none text-gold">&ldquo;</span>
                <p className="mt-2 font-accent text-lg italic leading-relaxed text-cream-50/90">
                  {t.quote}
                </p>
                <div className="mt-8">
                  <p className="font-display text-xl text-gold">{t.name}</p>
                  <p className="text-xs uppercase tracking-[0.2em] text-cream-50/50">
                    {t.role}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaBand() {
  return (
    <section className="relative overflow-hidden py-28 sm:py-40">
      <div className="mx-auto max-w-wide px-6 text-center sm:px-12">
        <PaisleyDivider className="mb-12" />
        <AnimatedHeading
          text="Your transformation awaits."
          className="mx-auto max-w-4xl font-display text-5xl font-light leading-tight text-cream-50 sm:text-7xl"
        />
        <Reveal delay={0.2}>
          <p className="mx-auto mt-6 max-w-xl text-cream-50/70">
            Reserve your seat at the atelier and let our artists craft a look that
            is unmistakably, radiantly you.
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <Link
            to="/contact"
            data-testid="cta-band-book"
            className="mt-10 inline-block rounded-full bg-gold px-10 py-5 text-xs uppercase tracking-[0.25em] text-ink-900 transition-colors hover:bg-antique"
          >
            Book Your Appointment
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <PageShell>
      <Hero />
      <div className="border-y border-gold/15 bg-ink-900 py-8">
        <Marquee
          items={[
            "Hair & Styling",
            "Skin & Spa",
            "Bridal & Makeup",
            "Nails & Hands",
            "Heritage Rituals",
          ]}
        />
      </div>
      <StorySection />
      <ServicesPreview />
      <GalleryStrip />
      <Testimonials />
      <CtaBand />
    </PageShell>
  );
}
